import React from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Yup from '../../../../../utils/yup';
import { StyledPaperContainer, StyledSectionTitle } from '../../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { ColumnWithGap } from '../../../../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';
import TextEditor from '../../../../FormEditors/TextEditor';
import ImageEditor from '../../../../FormEditors/ImageEditor';
import ColorEditor from '../../../../FormEditors/ColorEditor';
import { DangerButton, HealtheraButton } from '../../../../../healtheraComponents';
import useConditionSpotlight from '../../hooks/useConditionSpotlight';
import { ClinicConditionSpotlight } from '../../../types';

interface SpotlightFormValues {
  headline: string;
  subtitle: string;
  image_url: string;
  background_color: string;
  cta_label: string;
}

const schema = Yup.object().shape({
  headline: Yup.string().required(),
  subtitle: Yup.string().required(),
  image_url: Yup.string().required(),
  background_color: Yup.string().trim().matches(/^#(?:[0-9a-fA-F]{3}){1,2}$/i, 'Color is not a HEX value').required(),
  cta_label: Yup.string().required(),
});

interface ConditionSpotlightEditorProps {
  clinicId: string;
  clinicConditionId: string;
  spotlight?: ClinicConditionSpotlight | null;
  isSavedCondition: boolean;
  isDisabled?: boolean;
}

// Own local form, saved/deleted via the dedicated spotlight endpoint (the hook then refetches the clinic).
// Deliberately not seeded from the condition promo — the spotlight carries its own image/background/headline.
function ConditionSpotlightEditor({
  clinicId,
  clinicConditionId,
  spotlight,
  isSavedCondition,
  isDisabled,
}: ConditionSpotlightEditorProps) {
  const { upsertSpotlight, deleteSpotlight, isSaving, isDeleting } = useConditionSpotlight(clinicId);
  const hasExistingRow = Boolean(spotlight?._id);

  const form = useForm<SpotlightFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      headline: spotlight?.headline || '',
      subtitle: spotlight?.subtitle || '',
      image_url: spotlight?.image_url || '',
      background_color: spotlight?.background_color || '',
      cta_label: spotlight?.cta_label || '',
    },
  });

  const disabled = Boolean(isDisabled) || !isSavedCondition;

  const handleSave = async (values: SpotlightFormValues) => {
    await upsertSpotlight({ clinicConditionId, spotlight: values });
  };

  const handleDelete = async () => {
    await deleteSpotlight({ clinicConditionId });
  };

  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionTitle>Spotlight</StyledSectionTitle>
        {!isSavedCondition && <Notice>Save the condition first to add spotlight content.</Notice>}
        <TextEditor label="Headline" control={form.control} name="headline" disabled={disabled} />
        <TextEditor label="Subtitle" control={form.control} name="subtitle" disabled={disabled} />
        <ImageEditor
          control={form.control}
          name="image_url"
          label="Image (large transparent render)"
          suggestedSize="1125x630px"
          height="210px"
          width="375px"
        />
        <ColorEditor control={form.control} name="background_color" label="Background color (#hex)" />
        <TextEditor label="CTA label" control={form.control} name="cta_label" disabled={disabled} />
        <ButtonRow>
          <HealtheraButton disabled={disabled || isSaving} onClick={form.handleSubmit(handleSave)}>
            Save spotlight
          </HealtheraButton>
          {hasExistingRow && (
            <DangerButton disabled={disabled || isDeleting} onClick={handleDelete}>
              Remove spotlight
            </DangerButton>
          )}
        </ButtonRow>
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

const Notice = styled.div`
  padding: 12px 16px;
  background-color: #fff9c4;
  border-left: 4px solid #fbc02d;
  color: #424242;
  border-radius: 4px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;

export default ConditionSpotlightEditor;
